import React, { useState, useEffect } from 'react';
import { auth } from '../firebase/config';

/**
 * CSRFToken Component
 * 
 * This component generates a CSRF token for forms to prevent
 * Cross-Site Request Forgery attacks.
 * 
 * The token is based on the user's authentication state and a random value.
 */
const CSRFToken = ({ onTokenGenerated }) => {
  const [token, setToken] = useState('');

  useEffect(() => {
    const generateToken = async () => {
      try {
        // Get current user if authenticated
        const user = auth.currentUser;
        
        // Generate a random string
        const randomPart = Array.from(
          window.crypto.getRandomValues(new Uint8Array(32))
        )
          .map(byte => byte.toString(16).padStart(2, '0'))
          .join('');
        
        // Create timestamp
        const timestamp = Date.now().toString();
        
        // Combine user ID (if available), random string, and timestamp
        const userId = user ? user.uid : 'anonymous';
        const rawToken = `${userId}-${randomPart}-${timestamp}`;
        
        // Encode the token
        const encodedToken = btoa(rawToken);
        
        // Store token in session storage
        sessionStorage.setItem('csrfToken', encodedToken);
        
        // Set the token in state
        setToken(encodedToken);
        
        // Pass token to parent component if callback provided
        if (onTokenGenerated) {
          onTokenGenerated(encodedToken);
        }
      } catch (error) {
        console.error('Error generating CSRF token:', error);
      }
    };

    generateToken();
    
    // Regenerate token when auth state changes
    const unsubscribe = auth.onAuthStateChanged(() => {
      generateToken();
    });
    
    return () => unsubscribe();
  }, [onTokenGenerated]);

  return (
    <input type="hidden" name="csrfToken" value={token} />
  );
};

/**
 * Validates a CSRF token against the stored token
 * @param {string} token - The token to validate
 * @returns {boolean} - Whether the token is valid
 */
export const validateCSRFToken = (token) => {
  const storedToken = sessionStorage.getItem('csrfToken');
  return token === storedToken;
};

export default CSRFToken;
